import { datadogLogs } from '@datadog/browser-logs';
import { RemoveRedEye } from '@mui/icons-material';
import { Fare, Porter, User } from '@silbo/core';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBandwidthContext } from '../../../contexts/BandwidthContext';
import { useQueryQueueContext } from '../../../contexts/QueryQueueContext';
import useLiveQuery from '../../../hooks/useLiveQuery';
import { useTimeoutError } from '../../../hooks/useTimeoutError';
import ConfirmActionDialog, { ConfirmActionDialogProps } from '../components/ConfirmActionDialog';
import FareCard from '../components/FareCard';
import FareListContainer from '../components/FareListContainer';
import { defaultDialogConfig } from '../const/dialog';
import { useAcknowledgeFareMutation } from '../hooks/mutations/useAcknowledgeFareMutation';
import { useFareTickMutation } from '../hooks/mutations/useFareTickMutation';
import { useUpdateAssignationQueryData } from '../hooks/state-prediction/useUpdateAssignationQueryData';
import { useUpdateFareQueryData } from '../hooks/state-prediction/useUpdateFareQueryData';
import useFareUpdatedNotifications from '../hooks/toasts/useFareUpdatedNotifications';
import { useNewFareToast } from '../hooks/toasts/useNewFareToast';
import { getFaresQuery, getFaresQueryOptions } from '../queries';
import { FareStatusType } from '../types/fare-status';

interface ActiveFaresPageProps {
  porter: Porter | undefined;
  user?: User;
}

const ActiveFaresPage = ({ porter, user }: ActiveFaresPageProps) => {
  const updateQueryData = useUpdateFareQueryData(porter);
  const updateAssignationQueryData = useUpdateAssignationQueryData(porter);
  const [fareTickDialogConfig, setFareTickDialogConfig] = useState<ConfirmActionDialogProps>(defaultDialogConfig);
  const [acknowledgeFareDialogConfig, setAcknowledgeFareDialogConfig] =
    useState<ConfirmActionDialogProps>(defaultDialogConfig);
  const { t } = useTranslation('patient-transport');
  const [fareClicked, setFareClicked] = useState<{ fareId?: string; status?: string; date?: Date }>({});

  const queryClient = useQueryClient();

  const { data: fares, refetch } = useSuspenseQuery(getFaresQueryOptions(porter));
  useLiveQuery(getFaresQuery(porter));

  // Diagnostic logging for fare data loading: only log when the data is anomalous,
  // the nominal state on every fares change is pure noise.
  useEffect(() => {
    const faresWithMissingPatient = fares.filter((fare) => !fare.patient).length;
    const faresWithMissingGender = fares.filter((fare) => fare.patient && !fare.patient.gender).length;

    if (faresWithMissingPatient > 0 || faresWithMissingGender > 0) {
      datadogLogs.logger.warn('Fares data state: missing patient data', {
        faresCount: fares.length,
        hasPorter: !!porter,
        faresWithMissingPatient,
        faresWithMissingGender,
      });
    }
  }, [fares, porter]);

  useFareUpdatedNotifications(user);
  useNewFareToast(fares);

  const { addQuery, removeQuery } = useQueryQueueContext();
  const { status } = useBandwidthContext();
  const isOfflineMode = status === 'offline';

  const {
    acknowledgeFare,
    isPending: isAcknowledgeFarePending,
    isError: isAcknowledgeFareError,
    isSuccess: isAcknowledgeFareSuccess,
    error: acknowledgeFareError,
    reset: resetAcknowledgeFare,
  } = useAcknowledgeFareMutation({
    onSuccess: (_, fareId) => {
      datadogLogs.logger.debug('Acknowledge fare success callback', {
        fareId,
        fareIdType: typeof fareId,
        porter: porter?.id,
      });
      if (porter) {
        updateAssignationQueryData({ fareId: fareId as string });
      }
      removeQuery(JSON.stringify({ fareId }));
      refetch();
    },
    onError: (_, fareId) => {
      datadogLogs.logger.error('Acknowledge fare error callback', {
        fareId,
        fareIdType: typeof fareId,
      });
      removeQuery(JSON.stringify({ fareId }));
      refetch();
    },
  });

  const {
    fareTick,
    isPending: isFareTickPending,
    error: fareTickError,
    isError: isFareTickError,
    isSuccess: isFareTickSuccess,
    reset: resetFareTick,
  } = useFareTickMutation({
    onSuccess: async (_, params) => {
      datadogLogs.logger.debug('Fare tick success callback', {
        params,
        paramsType: typeof params,
        paramsKeys: params ? Object.keys(params) : 'null',
      });

      // Add defensive check for params
      if (!params || typeof params !== 'object') {
        datadogLogs.logger.error('Fare tick success: invalid params', { params });
        return;
      }

      const { fareId, status } = params as { fareId: string; status: FareStatusType; date: Date };
      updateQueryData(params as { fareId: string; status: FareStatusType; date: Date });
      removeQuery(JSON.stringify({ fareId, status }));
      refetch();
      if (status === 'PATIENT_CARE') {
        await queryClient.refetchQueries({ queryKey: ['getDailyFares'], type: 'active' });
      }
    },
    onError: (_, params) => {
      datadogLogs.logger.error('Fare tick error callback', {
        params,
        paramsType: typeof params,
        paramsKeys: params ? Object.keys(params) : 'null',
      });

      // Add defensive check for params
      if (!params || typeof params !== 'object') {
        datadogLogs.logger.error('Fare tick error: invalid params', { params });
        return;
      }

      const { fareId, status } = params as { fareId: string; status: FareStatusType; date: Date };
      removeQuery(JSON.stringify({ fareId, status }));
      refetch();
    },
  });

  const { isTimedOut: isAcknowledgeFareTimedOut, reset: resetAcknowledgeFareTimeout } =
    useTimeoutError(isAcknowledgeFarePending);
  const { isTimedOut: isFareTickTimedOut, reset: resetFareTickTimeout } = useTimeoutError(isFareTickPending);

  useEffect(() => {
    if (isAcknowledgeFareTimedOut) {
      datadogLogs.logger.info('Acknowledge fare timeout popin', fareClicked);
    }
  }, [isAcknowledgeFareTimedOut, fareClicked]);

  useEffect(() => {
    if (isFareTickTimedOut) {
      datadogLogs.logger.info('Fare tick timeout popin', fareClicked);
    }
  }, [isFareTickTimedOut, fareClicked]);

  const closeAcknowledgeFareModal = () => {
    resetAcknowledgeFare();
    resetAcknowledgeFareTimeout();
    setAcknowledgeFareDialogConfig(defaultDialogConfig);
  };

  const handleAcknowledge = (fare: Fare) => () => {
    resetAcknowledgeFareTimeout();
    datadogLogs.logger.debug('Fare acknowledged clicked', { fareId: fare.id, status: fare.status });
    setFareClicked({ fareId: fare.id, status: fare.status });

    datadogLogs.logger.debug('RACE_CONDITION_DEBUG: Adding acknowledgeFare to custom queue', {
      fareId: fare.id,
      timestamp: Date.now(),
      source: 'custom-queue',
      action: 'acknowledge',
    });

    addQuery({
      id: JSON.stringify({ fareId: fare.id }),
      queryFn: async () => {
        datadogLogs.logger.debug('RACE_CONDITION_DEBUG: Executing acknowledgeFare from custom queue', {
          fareId: fare.id,
          timestamp: Date.now(),
          source: 'custom-queue',
        });
        return acknowledgeFare(fare.id);
      },
    });
  };

  const openAcknowledgeModal = (fare: Fare) =>
    setAcknowledgeFareDialogConfig({
      opened: true,
      title: t('fare.actions.acknowledge'),
      content: () => <RemoveRedEye />,
      onConfirm: handleAcknowledge(fare),
      onClose: closeAcknowledgeFareModal,
    });

  const closeFareTickModal = () => {
    resetFareTick();
    resetFareTickTimeout();
    setFareTickDialogConfig(defaultDialogConfig);
  };

  const handleFareTick = (fare: Fare) => () => {
    resetFareTickTimeout();
    const params = { fareId: fare.id, status: fare.status, date: new Date() };
    datadogLogs.logger.debug('Fare tick clicked', { fareId: fare.id, status: fare.status, date: new Date() });
    setFareClicked(params);

    datadogLogs.logger.debug('RACE_CONDITION_DEBUG: Adding fareTick to custom queue', {
      fareId: fare.id,
      status: params.status,
      timestamp: Date.now(),
      source: 'custom-queue',
      action: 'fareTick',
    });

    addQuery({
      id: JSON.stringify({ fareId: fare.id, status: params.status }),
      queryFn: async () => {
        datadogLogs.logger.debug('RACE_CONDITION_DEBUG: Executing fareTick from custom queue', {
          fareId: fare.id,
          status: params.status,
          timestamp: Date.now(),
          source: 'custom-queue',
        });
        return fareTick(params);
      },
    });
  };

  const openFareTickModal = (fare: Fare, title: string, content: () => JSX.Element) =>
    setFareTickDialogConfig({
      opened: true,
      title,
      content,
      onConfirm: handleFareTick(fare),
      onClose: closeFareTickModal,
    });

  return (
    <>
      <ConfirmActionDialog
        {...acknowledgeFareDialogConfig}
        isLoading={isAcknowledgeFarePending}
        isError={!isAcknowledgeFareSuccess && (isAcknowledgeFareError || isAcknowledgeFareTimedOut)}
        error={isAcknowledgeFareTimedOut ? t('errors.timeoutError', { ns: 'common' }) : acknowledgeFareError?.message}
        isSuccess={isAcknowledgeFareSuccess}
        isOfflineMode={isOfflineMode}
      />
      <ConfirmActionDialog
        {...fareTickDialogConfig}
        isLoading={isFareTickPending}
        isError={!isFareTickSuccess && (isFareTickError || isFareTickTimedOut)}
        error={isFareTickTimedOut ? t('errors.timeoutError', { ns: 'common' }) : fareTickError?.message}
        isSuccess={isFareTickSuccess}
        isOfflineMode={isOfflineMode}
      />
      <FareListContainer>
        {fares?.map((fare) => (
          <FareCard
            key={fare.id}
            fare={fare}
            isOfflineMode={isOfflineMode}
            onFareTick={openFareTickModal}
            onAcknowledge={openAcknowledgeModal}
          />
        ))}
      </FareListContainer>
      <Outlet />
    </>
  );
};

export default ActiveFaresPage;
